import React, { ChangeEvent, useState } from 'react';
import { history } from '../../../utils';
import { VALID_EMAIL_REGEX } from '../../../util/regExp';
import { ADMIN_FLAG_TO_ROLE_NAME, READ_ONLY_ROLE_NAME } from '../../../constants';
import {
  MovaticFieldGroupCheck,
  MovaticFieldGroupText,
} from '../../../components/Redesing/movatic-field-group';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PageContainer from '../../../components/Redesing/page-container';
import CardView from '../../../components/Redesing/card-view';
import { Stack } from '@mui/system';
import Typography from '@mui/material/Typography';
import CustomToast from '../../../components/Redesing/custom-toast';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import { inviteAdminToOrganizationMutation } from '../../../hooks/admins/mutation/V2';
import useOrganization from '../../../hooks/use-organization';
import {
  PERMISSION_FIELDS,
  PermissionFlags,
  buildPermissionFlags,
  emptyPermissionFlags,
} from '../../../util/adminPermissions';

const OrganizationAddAdmin: React.FC = () => {
  const organizationInfo = useOrganization();

  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<PermissionFlags>(emptyPermissionFlags());
  const [buttonDisabled, setButtonDisabled] = useState(false);

  // Mirrors the system admin form's toggle logic: checking "Admin" grants every
  // permission, while touching any other box clears the "Admin" master flag.
  const handlePermissionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (name !== 'admin' || !checked) {
      setPermissions((prev) => ({
        ...prev,
        admin: false,
        [name]: checked,
      }));
    } else {
      setPermissions(buildPermissionFlags(true));
    }
  };

  const canSubmit = () => {
    if (firstName.trim().length <= 0) {
      setFirstNameError('First name is required');
      return false;
    }
    setFirstNameError(null);

    if (lastName.trim().length <= 0) {
      setLastNameError('Last name is required');
      return false;
    }
    setLastNameError(null);

    if (email.trim().length <= 0) {
      setEmailError('Email is required');
      return false;
    }

    if (!VALID_EMAIL_REGEX.test(email)) {
      setEmailError('Enter a valid email address');
      return false;
    }
    setEmailError(null);

    return true;
  };

  // Adds the admin through ant's `inviteAdminToOrganization`, in one request: it
  // creates the account when the email is new (ant emails the set-password link) or
  // links an existing one, creates the organization membership with its config, and
  // grants a role per selected permission flag plus the baseline "Read Only" role.
  // The legacy REST `POST /organization/<id>/admin` only linked an existing account
  // (a brand-new email failed with "Administrator user not found"), so it is no
  // longer called.
  const save = async () => {
    if (!canSubmit()) {
      CustomToast({
        type: 'error',
        message: 'There was an error, please check your form and try again',
      });
      return;
    }

    const organizationId = organizationInfo?.id;
    if (!organizationId) {
      CustomToast({
        type: 'error',
        message: 'The organization is still loading, please try again in a moment',
      });
      return;
    }

    setButtonDisabled(true);

    // Map the selected permission flags to GraphQL role names, always granting
    // the baseline "Read Only" role.
    const roleNames = PERMISSION_FIELDS.filter((field) => permissions[field.name])
      .map((field) => ADMIN_FLAG_TO_ROLE_NAME[field.name])
      .filter(Boolean);
    if (!roleNames.includes(READ_ONLY_ROLE_NAME)) {
      roleNames.push(READ_ONLY_ROLE_NAME);
    }

    const trimmedEmail = email.toLowerCase().trim();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const config = {
      details: {
        created_on: new Date().toISOString(),
        last_access: new Date().toISOString(),
        name: `${trimmedFirstName} ${trimmedLastName}`.trim(),
      },
    };

    // CustomAsyncToast rejects when the work does, so a failure lands in the catch.
    const invited = await CustomAsyncToast({
      promise: () =>
        inviteAdminToOrganizationMutation({
          email: trimmedEmail,
          organizationId,
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          config,
          roleNames,
        }).then((invite) => {
          if (!invite.isSuccess) {
            throw new Error(invite.errors[0]?.message || 'Adding the admin failed');
          }
          return invite.data;
        }),
      successMessage: () => 'Admin was added',
      loadingMessage: 'Adding admin...',
      errorMessage: 'There was an issue while adding this admin, please try again',
    }).catch(() => null);

    if (invited) {
      history.push({
        pathname: '/settings/admin',
        search: `?organizationId=${organizationId}`,
      });
    } else {
      setButtonDisabled(false);
    }
  };

  const content = () => (
    <div>
      <Typography variant="h6">Admin Information</Typography>
      <MovaticFieldGroupText
        id="firstName"
        name="firstName"
        label="First Name"
        description="Admin's first name"
        hasError={Boolean(firstNameError)}
        errorMessage={firstNameError}
        value={firstName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setFirstName(e.target.value);
        }}
      />

      <MovaticFieldGroupText
        id="lastName"
        name="lastName"
        label="Last Name"
        description="Admin's last name"
        hasError={Boolean(lastNameError)}
        errorMessage={lastNameError}
        value={lastName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setLastName(e.target.value);
        }}
      />

      <MovaticFieldGroupText
        id="email"
        name="email"
        label="Email"
        description={`Email of the admin you want to add to ${
          organizationInfo?.name ?? 'this organization'
        }`}
        hasError={Boolean(emailError)}
        errorMessage={emailError}
        value={email}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setEmail(e.target.value);
        }}
      />

      <Typography variant="h6">Admin Permissions</Typography>
      {PERMISSION_FIELDS.map((field) => (
        <MovaticFieldGroupCheck
          key={field.name}
          id={field.name}
          name={field.name}
          label={field.label}
          description={field.description}
          onChange={handlePermissionChange}
          checked={permissions[field.name]}
        />
      ))}
    </div>
  );

  const button = () => (
    <IconButtonMenu
      buttons={[
        {
          label: 'Cancel',
          onClick: () =>
            history.push({
              pathname: '/settings/admin',
              search: `?organizationId=${organizationInfo?.id}`,
            }),
          startIcon: <CloseIcon />,
        },
        {
          label: 'Save',
          onClick: save,
          startIcon: <SaveIcon />,
          disabled: buttonDisabled,
        },
      ]}
    />
  );

  return (
    <PageContainer isLoading={!organizationInfo?.id}>
      <CardView
        content={
          <Stack paddingX={2} paddingBottom={2}>
            {content()}
          </Stack>
        }
        title="Add Admin User to Organization"
        headerActions={button()}
      />
    </PageContainer>
  );
};

export default OrganizationAddAdmin;
